import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Plus, X, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { confirmDialog } from "@/components/ConfirmDialog";

const TYPES = ["meeting", "call", "email", "note"];
const empty = { type: "meeting", summary: "", date: "", contact_id: "", deal_id: "" };

function PageHeader({ overline, title, action }) {
  return (
    <div className="border-b border-navy/10 px-8 py-8 bg-cream flex items-end justify-between">
      <div>
        <div className="overline text-gold-deep">{overline}</div>
        <h1 className="font-serif text-3xl md:text-4xl text-navy mt-2">{title}</h1>
        <div className="gold-rule mt-4" />
      </div>
      {action}
    </div>
  );
}

export default function Interactions() {
  const [items, setItems] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [deals, setDeals] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const [i, c, d] = await Promise.all([api.get("/interactions"), api.get("/contacts"), api.get("/deals")]);
    setItems(i.data); setContacts(c.data); setDeals(d.data);
  };
  useEffect(() => { load(); }, []);

  const contactName = (id) => contacts.find((c) => c.id === id)?.name || "—";
  const dealName = (id) => deals.find((d) => d.id === id)?.name || "—";

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, contact_id: form.contact_id || null, deal_id: form.deal_id || null, date: form.date || null };
      await api.post("/interactions", payload);
      toast.success("Interaction logged");
      setShowForm(false);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!(await confirmDialog({ title: "Delete this interaction?", description: "The log entry will be permanently removed.", confirmLabel: "Delete", destructive: true }))) return;
    await api.delete(`/interactions/${id}`);
    load();
  };

  return (
    <div data-testid="interactions-page">
      <PageHeader
        overline="Log"
        title="Interactions"
        action={
          <button onClick={() => { setForm(empty); setShowForm(true); }} data-testid="interactions-add-btn" className="bg-navy text-gold px-4 py-2.5 text-xs uppercase tracking-widest hover:bg-navy-600 flex items-center gap-2">
            <Plus className="h-3.5 w-3.5" /> Log interaction
          </button>
        }
      />
      <div className="p-8">
        <div className="bg-white border border-navy/10">
          {items.length === 0 && (
            <div className="p-12 text-center text-navy/40 italic">No interactions yet.</div>
          )}
          {items.map((i) => (
            <div key={i.id} className="border-b border-navy/5 last:border-b-0 p-5 flex items-start gap-5" data-testid={`interaction-${i.id}`}>
              <div className="flex-shrink-0">
                <div className="overline text-gold-deep">{i.type}</div>
                <div className="text-xs text-navy/50 mt-1">{(i.date || i.created_at || "").slice(0, 10)}</div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-navy/90">{i.summary}</div>
                <div className="text-xs text-navy/50 mt-1 flex gap-4 flex-wrap">
                  {i.contact_id && <span>Contact: {contactName(i.contact_id)}</span>}
                  {i.deal_id && <span>Deal: {dealName(i.deal_id)}</span>}
                </div>
              </div>
              <button onClick={() => remove(i.id)} className="text-navy/40 hover:text-destructive">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-navy/40 flex items-center justify-center p-4" data-testid="interaction-form-modal">
          <form onSubmit={save} className="bg-white max-w-xl w-full p-8">
            <div className="flex justify-between mb-6">
              <div>
                <div className="overline text-gold-deep">New</div>
                <h2 className="font-serif text-2xl text-navy">Log interaction</h2>
              </div>
              <button type="button" onClick={() => setShowForm(false)} className="text-navy/50">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-5">
                <div>
                  <label className="overline text-navy/60">Type</label>
                  <select data-testid="interaction-input-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
                    className="w-full mt-1.5 border-b border-navy/20 bg-transparent py-2 focus:outline-none focus:border-gold">
                    {TYPES.map((t) => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="overline text-navy/60">Date</label>
                  <input type="date" data-testid="interaction-input-date" value={form.date || ""} onChange={(e) => setForm({ ...form, date: e.target.value })}
                    className="w-full mt-1.5 border-b border-navy/20 bg-transparent py-2 focus:outline-none focus:border-gold" />
                </div>
                <div>
                  <label className="overline text-navy/60">Contact</label>
                  <select data-testid="interaction-input-contact" value={form.contact_id || ""} onChange={(e) => setForm({ ...form, contact_id: e.target.value })}
                    className="w-full mt-1.5 border-b border-navy/20 bg-transparent py-2 focus:outline-none focus:border-gold">
                    <option value="">— None —</option>
                    {contacts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="overline text-navy/60">Deal</label>
                  <select data-testid="interaction-input-deal" value={form.deal_id || ""} onChange={(e) => setForm({ ...form, deal_id: e.target.value })}
                    className="w-full mt-1.5 border-b border-navy/20 bg-transparent py-2 focus:outline-none focus:border-gold">
                    <option value="">— None —</option>
                    {deals.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="overline text-navy/60">Summary *</label>
                <textarea required rows={4} data-testid="interaction-input-summary" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })}
                  className="w-full mt-1.5 border border-navy/15 bg-transparent p-3 focus:outline-none focus:border-gold resize-none text-sm" />
              </div>
            </div>
            <div className="mt-8 flex justify-end gap-3">
              <button type="button" onClick={() => setShowForm(false)} className="px-5 py-2.5 text-xs uppercase tracking-widest text-navy/70">Cancel</button>
              <button type="submit" data-testid="interaction-form-save" className="bg-navy text-gold px-6 py-2.5 text-xs uppercase tracking-widest hover:bg-navy-600">
                Log
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
